import {BigNumber} from '@ttx/core';
import {NextRouter} from 'next/router';
import {PaymentDetailsFormProps} from '../../components/purchase-or-download-cta/purchase-cta/cash-payment-details-modal/cash-payment-details-modal';
import {mixpanelTrack} from '../../contexts/analytics-context/utils';
import {ENVIRONMENT} from '../../contexts/authentication-context/connectors';
import {traxxStemzSlug, emilGSlug} from '../../utils/consts';
import {CirclePay} from '../../utils/payment/circle-pay-api';
import {ModalVariant} from '../../utils/types';

interface BuyWithCashProps {
  values: PaymentDetailsFormProps;
  ipAddress?: string;
  cashTotal: string | null;
  url: string;
  setModalVariant: (variant: ModalVariant | null) => void;
  checkout: CirclePay;
  userId: string | null;
  id?: string;
  listingId: string;
  purchaseQuantity: number;
  buyoutPrice: BigNumber;
  router: NextRouter;
}

export const buyWithCash: (props: BuyWithCashProps) => Promise<void> = async ({
  values,
  ipAddress,
  cashTotal,
  url,
  setModalVariant,
  checkout,
  userId,
  id,
  listingId,
  purchaseQuantity,
  buyoutPrice,
  router,
}) => {
  setModalVariant('cash-pending');
  const isEmilGPage = router.asPath.includes('emilg');
  const isStemzPage = router.asPath.includes('revolution');
  if (!values || !ipAddress || !cashTotal || !url)
    throw new Error('Missing values required for checkout');
  try {
    const {sessionId} = checkout;

    const encryptedCardDetails = await checkout.encryptCardDetails({
      number: values.cardNumber.trim(),
      cvv: values.cvv,
    });

    const metadata = {
      email: values.email,
      sessionId,
      ipAddress,
    };

    const details = {
      billingDetails: {
        name: values.cardholderName,
        city: values.city,
        country: values.country,
        district: values.state,
        line1: values.billingLine1,
        line2: values.billingLine2,
        postalCode: values.postCode,
      },
      metadata,
      encryptedData: encryptedCardDetails.encryptedMessage,
      keyId: encryptedCardDetails.keyId,
      expMonth: Number(values.expiryMonth),
      expYear:
        values.expiryYear?.length === 2
          ? Number(`20${values.expiryYear}`)
          : Number(values.expiryYear),
    };

    const paymentDetails = {
      metadata,
      amount: {
        amount: cashTotal?.replace('$', ''),
        currency: 'USD',
      },
      autoCapture: true,
      keyId: encryptedCardDetails.keyId,
      // The verification redirect only works on hosted envs so for local, we don't use 3D secure
      verification: ENVIRONMENT === 'local' ? 'none' : 'three_d_secure',
      verificationSuccessUrl: ENVIRONMENT === 'local' ? '' : `https://${url}`,
      verificationFailureUrl: ENVIRONMENT === 'local' ? '' : `https://${url}`,
      description: JSON.stringify({
        userId,
        nftId: id,
        // TODO: what would the listing ID be for fiat payment
        listingId,
        quantity: purchaseQuantity,
        price: buyoutPrice?.toString(),
        dropType: 'g6ts',
      }),
      idempotencyKey: sessionId,
    };
    const card = await checkout.createCard(details);

    await checkout
      .createPayment({
        ...paymentDetails,
        source: {type: 'card', id: card.id},
      })
      .then((data) => {
        // For local envs only - automatically handled by 3DS in hosted envs
        if (isStemzPage && data.id) {
          router.push(`/traxxstemz/${traxxStemzSlug}?paymentId=${data.id}`);
        } else if (isEmilGPage && data.id) {
          router.push(`/emilg/${emilGSlug}?paymentId=${data.id}`);
        }
      });
  } catch (e: any) {
    mixpanelTrack('Fiat purchases failed');
    setModalVariant('cash-failed');
  }
};
